import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Eye, EyeOff, Mail, Lock, CheckCircle } from 'lucide-react'
import { Button, Input, Card } from '../ui'
import { useAuthStore } from '../../stores/authStore'
import ForgotPasswordForm from './ForgotPasswordForm'
import { isSubscriptionsEnabled } from '../../utils/featureFlags'
import { useAuthTracking } from '../../contexts/AnalyticsContext'

const AUTH_API_URL =
  import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3005/api/v1'

const LoginForm: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [showVerifiedMessage, setShowVerifiedMessage] = useState(false)
  const [returnUrl, setReturnUrl] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { error, clearError, setError } = useAuthStore()
  const { trackLoginSuccess, trackLoginFailed } = useAuthTracking()

  // Extract query parameters on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const returnUrlParam = urlParams.get('returnUrl')
    const verified = urlParams.get('verified')

    if (returnUrlParam) {
      setReturnUrl(returnUrlParam)
    }

    if (verified === 'true') {
      setShowVerifiedMessage(true)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    setIsSubmitting(true)

    try {
      // Build API URL with returnUrl if present
      const apiUrl = returnUrl
        ? `${AUTH_API_URL}/auth/login?returnUrl=${encodeURIComponent(
            returnUrl
          )}`
        : `${AUTH_API_URL}/auth/login`

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Important for session cookies
        body: JSON.stringify({ email, password, rememberMe })
      })

      const data = await response.json()

      if (!response.ok || !data.success) {
        // Handle error response
        let errorMessage = 'Login failed'

        if (data.error?.message) {
          // Handle specific API error messages with user-friendly alternatives
          const apiError = data.error.message.toLowerCase()
          if (
            apiError.includes('invalid credentials') ||
            apiError.includes('invalid email') ||
            apiError.includes('invalid password')
          ) {
            errorMessage =
              'Invalid email or password. Please check your credentials and try again.'
          } else if (
            apiError.includes('account locked') ||
            apiError.includes('locked')
          ) {
            errorMessage =
              'Your account has been temporarily locked. Please contact support or try again later.'
          } else if (apiError.includes('too many attempts')) {
            errorMessage =
              'Too many login attempts. Please wait a few minutes before trying again.'
          } else {
            errorMessage = data.error.message
          }
        } else if (data.message) {
          errorMessage = data.message
        }

        setIsSubmitting(false)
        setError(errorMessage)
        return
      }

      if (data.success) {
        // Track successful login
        trackLoginSuccess(rememberMe)

        // Don't update auth state here - let checkAuth() handle it after redirect
        // The session cookie will be set by the backend, and checkAuth() will restore the auth state

        // Determine redirect based on onboarding status (priority over returnUrl)
        const { user } = data
        let redirectTo: string

        // Legacy flow: When subscriptions disabled, always go to dashboard
        if (!isSubscriptionsEnabled()) {
          redirectTo = '/'
        } else if (
          user?.onboardingStatus === 'completed' ||
          user?.onboardingStatus === 'admin_created' ||
          user?.roleName === 'system_admin'
        ) {
          // Onboarding complete - normal login flow to social-media view
          // Root path (/) will show social-media dashboard when authenticated
          redirectTo = '/'
        } else {
          // Onboarding incomplete - use redirectUrl from backend or returnUrl parameter
          redirectTo = data.redirectUrl || returnUrl || '/onboarding/checkout'
        }

        // Add a delay to ensure session cookie is set before redirect
        setTimeout(() => {
          window.location.href = redirectTo
        }, 500)
      }
    } catch (err: any) {
      // Track failed login
      trackLoginFailed(err?.message || 'Login failed')
      // Error will be shown via authStore
      // Handle network errors with user-friendly messages
      let errorMessage = 'Login failed'

      if (err instanceof Error) {
        if (
          err.message.toLowerCase().includes('failed to fetch') ||
          err.message.toLowerCase().includes('network')
        ) {
          errorMessage =
            'Unable to connect to the server. Please check your internet connection and try again.'
        } else if (err.message.toLowerCase().includes('timeout')) {
          errorMessage = 'The request timed out. Please try again.'
        } else {
          errorMessage = err.message
        }
      }

      setIsSubmitting(false)
      setError(errorMessage)
    }
  }

  const handleForgotPassword = () => {
    setShowForgotPassword(true)
    clearError()
  }

  const handleBackToLogin = () => {
    setShowForgotPassword(false)
    clearError()
  }

  // Show forgot password form if requested
  if (showForgotPassword) {
    return <ForgotPasswordForm onBack={handleBackToLogin} />
  }

  return (
    <div className='relative min-h-screen flex items-start justify-center bg-gray-50 pt-20 pb-20 px-4 sm:px-6 lg:px-8'>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className='max-w-md w-full space-y-8'
      >
        <div>
          <div className='mx-auto flex items-center justify-center mb-16'>
            <img
              src='/avi-logo-white-crop.png'
              alt='AVI by Athletiverse'
              className='w-64 h-auto'
            />
          </div>
          <h2 className='text-center text-3xl font-bold text-gray-900'>
            Sign in to your account
          </h2>
          <p className='mt-2 text-center text-sm text-gray-600'>Welcome back</p>
        </div>

        <Card className='p-8'>
          <form className='space-y-6' onSubmit={handleSubmit}>
            {showVerifiedMessage && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className='bg-green-50 border border-green-200 rounded-lg p-3'
              >
                <div className='flex items-center'>
                  <CheckCircle className='w-5 h-5 text-green-600 mr-2' />
                  <p className='text-sm text-green-800 font-medium'>
                    Email verified successfully! Please log in to continue.
                  </p>
                </div>
              </motion.div>
            )}

            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className='bg-red-50 border border-red-200 rounded-lg p-3'
              >
                <div className='flex items-center'>
                  <div className='text-red-600 mr-2'>⚠</div>
                  <p className='text-sm text-red-800'>{error}</p>
                </div>
              </motion.div>
            )}

            <div className='space-y-4'>
              <div>
                <Input
                  id='email'
                  label='Email address'
                  type='email'
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder='Enter your email'
                  icon={<Mail className='h-4 w-4' />}
                  iconPosition='left'
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label
                  htmlFor='password'
                  className='block text-sm font-medium text-gray-700 mb-1'
                >
                  Password
                </label>
                <div className='relative'>
                  <Input
                    id='password'
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder='Enter your password'
                    icon={<Lock className='h-4 w-4' />}
                    iconPosition='left'
                    disabled={isSubmitting}
                  />
                  <button
                    type='button'
                    className='absolute inset-y-0 right-0 pr-3 flex items-center z-10'
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isSubmitting}
                  >
                    {showPassword ? (
                      <EyeOff className='h-5 w-5 text-gray-400 hover:text-gray-600' />
                    ) : (
                      <Eye className='h-5 w-5 text-gray-400 hover:text-gray-600' />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className='flex items-center justify-between'>
              <div className='flex items-center'>
                <input
                  id='remember-me'
                  name='remember-me'
                  type='checkbox'
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className='h-4 w-4 text-avi-primary focus:ring-avi-primary border-gray-300 rounded'
                  disabled={isSubmitting}
                />
                <label
                  htmlFor='remember-me'
                  className='ml-2 block text-sm text-gray-900'
                >
                  Remember me
                </label>
              </div>

              <div className='text-sm'>
                <button
                  type='button'
                  onClick={handleForgotPassword}
                  className='font-medium text-avi-primary hover:text-avi-primary/80'
                  disabled={isSubmitting}
                >
                  Forgot your password?
                </button>
              </div>
            </div>

            <Button
              type='submit'
              className='w-full'
              disabled={isSubmitting || !email || !password}
            >
              {isSubmitting ? (
                <div className='flex items-center'>
                  <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2'></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </Button>
          </form>
        </Card>
      </motion.div>

      <footer className='absolute bottom-6 left-0 right-0 text-center text-xs text-gray-400 space-y-1'>
        <p>
          <Link to='/privacy' className='hover:text-gray-600 transition-colors'>Privacy Policy</Link>
          <span className='mx-2'>·</span>
          <Link to='/terms' className='hover:text-gray-600 transition-colors'>Terms of Service</Link>
        </p>
        <p>© {new Date().getFullYear()} Athletiverse, Inc. All rights reserved.</p>
      </footer>
    </div>
  )
}

export default LoginForm
